import React from 'react';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    Icon,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Stack,
    Text,
    TextField,
    useTheme,
} from '@fluentui/react';
import {
    assistantErrorMessage,
    useCreateStorefrontGenerationMutation,
} from '../../../store/endpoints/AssistantEndpoints';
import {useGetStorefrontsQuery} from '../../../store/endpoints/StorefrontSettingsEndpoints';
import {defaultSubUrlFor, isPlausibleWebsiteUrl, normalizeWebsiteUrl} from './aiStorefrontCreateLogic';

interface IAiStorefrontCreateDialogProps {
    onDismiss: () => void;
    /** The generation was queued; the flow moves to the progress view for this session. */
    onStarted: (sessionId: string) => void;
}

const WEBSITE_HINT = 'Enter a full website address, for example https://www.example.com';
const START_FAILED = 'The storefront could not be started. Please try again.';

/**
 * The whole of the guided create form: a website to copy the look from, where the storefront should
 * live, and anything the user wants to say about it.
 *
 * There is no model picker and no chat here on purpose. Everything else the assistant needs it
 * works out from the site.
 */
const AiStorefrontCreateDialog: React.FC<IAiStorefrontCreateDialogProps> = (props) => {
    const theme = useTheme();
    const [createGeneration] = useCreateStorefrontGenerationMutation();
    const storefronts = useGetStorefrontsQuery();

    const [websiteUrl, setWebsiteUrl] = React.useState('');
    const [subUrl, setSubUrl] = React.useState('');
    const [comments, setComments] = React.useState('');
    const [websiteError, setWebsiteError] = React.useState<string | undefined>();
    const [subUrlError, setSubUrlError] = React.useState<string | undefined>();
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
    const [submitting, setSubmitting] = React.useState(false);

    const storefrontCount = storefronts.data?.total ?? storefronts.data?.items?.length;

    // The suggestion lands once, when the count first resolves. After that the field is the user's:
    // a later refetch must not overwrite what they typed.
    const prefilled = React.useRef(false);
    React.useEffect(() => {
        if (prefilled.current || storefrontCount === undefined) return;
        prefilled.current = true;
        const suggested = defaultSubUrlFor(storefrontCount);
        if (suggested) setSubUrl(suggested);
    }, [storefrontCount]);

    // Typing "acme.com" and tabbing out should leave a real address in the box, not a validation
    // complaint about a scheme most people never type.
    const blurWebsite = () => {
        const normalized = normalizeWebsiteUrl(websiteUrl);
        if (normalized !== websiteUrl) setWebsiteUrl(normalized);
        setWebsiteError(!normalized || isPlausibleWebsiteUrl(normalized) ? undefined : WEBSITE_HINT);
    };

    const submit = async () => {
        const website = normalizeWebsiteUrl(websiteUrl);
        setWebsiteUrl(website);
        const trimmedSubUrl = subUrl.trim();

        const badWebsite = !isPlausibleWebsiteUrl(website);
        const badSubUrl = trimmedSubUrl === '';
        setWebsiteError(badWebsite ? WEBSITE_HINT : undefined);
        setSubUrlError(badSubUrl ? 'A sub-URL is required' : undefined);
        if (badWebsite || badSubUrl) return;

        setSubmitting(true);
        setErrorMessage(undefined);
        try {
            const session = await createGeneration({
                websiteUrl: website,
                subUrl: trimmedSubUrl,
                comments: comments.trim() || undefined,
            }).unwrap();
            if (session?.id) {
                setSubmitting(false);
                props.onStarted(session.id);
                return;
            }
            setErrorMessage(START_FAILED);
        } catch (error) {
            // A taken sub-URL, a missing entitlement and a spent quota all arrive here as an
            // ActionResponse whose message is written to be shown as-is.
            setErrorMessage(assistantErrorMessage(error, START_FAILED));
        }
        setSubmitting(false);
    };

    return (
        <Dialog
            hidden={false}
            onDismiss={() => {
                if (!submitting) props.onDismiss();
            }}
            minWidth={520}
            dialogContentProps={{
                title: 'Create with Adaptive Intelligence',
                showCloseButton: true,
            }}
            modalProps={{isBlocking: true}}
        >
            <Stack tokens={{childrenGap: '1em'}}>
                <Stack horizontal tokens={{childrenGap: '0.5em'}} verticalAlign='start'>
                    <Icon iconName='Robot' styles={{root: {fontSize: 20, color: theme.palette.themePrimary}}}/>
                    <Text variant='small'>
                        Point us at your website and we will build a storefront that matches it. This
                        takes a few minutes, and nothing goes live until you have looked at it and
                        approved it.
                    </Text>
                </Stack>

                {errorMessage && (
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage(undefined)}>
                        {errorMessage}
                    </MessageBar>
                )}

                <TextField
                    label='Website address'
                    required
                    autoFocus
                    placeholder='https://www.example.com'
                    value={websiteUrl}
                    errorMessage={websiteError}
                    disabled={submitting}
                    onChange={(_, val) => {
                        setWebsiteUrl(val ?? '');
                        if (websiteError) setWebsiteError(undefined);
                    }}
                    onBlur={blurWebsite}
                />

                <TextField
                    label='Storefront sub-URL'
                    required
                    value={subUrl}
                    errorMessage={subUrlError}
                    disabled={submitting}
                    description='The address your customers use, for example yourcompany.com/store'
                    onChange={(_, val) => {
                        setSubUrl(val ?? '');
                        if (subUrlError) setSubUrlError(undefined);
                    }}
                />

                <TextField
                    label='Anything we should know?'
                    multiline
                    rows={3}
                    value={comments}
                    disabled={submitting}
                    placeholder='Optional. Tell us about your brand, the colors to lean on, or the feel you are after.'
                    onChange={(_, val) => setComments(val ?? '')}
                />
            </Stack>
            <DialogFooter>
                <PrimaryButton
                    text={submitting ? 'Starting...' : 'Build my storefront'}
                    iconProps={{iconName: 'Robot'}}
                    disabled={submitting}
                    onClick={submit}
                />
                <DefaultButton text='Cancel' disabled={submitting} onClick={props.onDismiss}/>
            </DialogFooter>
        </Dialog>
    );
};

export default AiStorefrontCreateDialog;
